import React from "react";
import { FiBookOpen, FiImage, FiPlus, FiSave } from "react-icons/fi";

import { toAbsolutePublicUrl } from "../../../../services/mediaLibraryServices";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";
import AdminField from "../../_shared/ui/AdminField";
import AdminRichTextEditor from "../../_shared/ui/AdminRichTextEditor";

import { PUBLICATIONS_FORM_HELPERS } from "../data/publicationsAdminData";

export default function PublicationsFormDrawer({
  open,
  form,
  fieldErrors,
  saving,
  isEdit,
  onClose,
  onChange,
  onSave,
  onOpenMediaPicker,
}) {
  const previewUrl = form.image_publication_url
    ? toAbsolutePublicUrl(form.image_publication_url)
    : "";

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier la publication" : "Nouvelle publication"}
      subtitle={
        isEdit
          ? "Mettez à jour la publication puis enregistrez les modifications."
          : "Ajoutez un document, une brochure, un bulletin ou une plaquette."
      }
      icon={FiBookOpen}
      size="xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <AdminButton variant="secondary" onClick={onClose}>
            Annuler
          </AdminButton>

          <AdminButton
            icon={isEdit ? FiSave : FiPlus}
            variant="primary"
            loading={saving}
            onClick={onSave}
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </AdminButton>
        </div>
      }
    >
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <div className="space-y-5">
          <AdminField
            label="Titre de la publication"
            required
            error={fieldErrors.titre}
            help="Exemple : Bulletin municipal, plaquette, programme..."
          >
            <input
              value={form.titre}
              onChange={(event) => onChange("titre", event.target.value)}
              placeholder="Titre affiché sur le site"
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>

          <AdminField
            label="Image"
            help="Vous pouvez saisir une URL ou choisir une image dans la photothèque."
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                value={form.image_publication_url}
                onChange={(event) =>
                  onChange("image_publication_url", event.target.value)
                }
                placeholder="https://... ou /uploads/..."
                className="min-h-12 flex-1 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />

              <AdminButton
                icon={FiImage}
                variant="soft"
                onClick={onOpenMediaPicker}
                className="sm:min-w-[180px]"
              >
                Photothèque
              </AdminButton>
            </div>
          </AdminField>

          <AdminField
            label="Lien de la publication"
            help="Lien vers un PDF, une page interne ou un site externe."
          >
            <input
              value={form.lien_publication_url}
              onChange={(event) =>
                onChange("lien_publication_url", event.target.value)
              }
              placeholder="/uploads/documents/bulletin.pdf ou https://..."
              className="min-h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
            />
          </AdminField>

          <AdminField
            label="Description"
            help="Utilisez les boutons de mise en forme pour structurer le texte."
          >
            <AdminRichTextEditor
              value={form.description}
              onChange={(html) => onChange("description", html)}
              placeholder="Présentez brièvement le document, son contenu ou son utilité..."
            />
          </AdminField>
        </div>

        <aside className="space-y-5">
          <div className="overflow-hidden rounded-[1.7rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
              <p className="text-sm font-bold text-slate-950 dark:text-white">
                Aperçu image
              </p>
            </div>

            <div className="p-4">
              {previewUrl ? (
                <>
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="aspect-[16/10] w-full rounded-[1.25rem] object-cover"
                  />

                  <p className="mt-3 break-all text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {previewUrl}
                  </p>
                </>
              ) : (
                <div className="flex aspect-[16/10] flex-col items-center justify-center rounded-[1.25rem] bg-white text-center text-slate-400 dark:bg-slate-900 dark:text-slate-500">
                  <FiImage className="text-4xl" />

                  <p className="mt-3 text-sm font-bold">Aucune image</p>

                  <p className="mt-1 max-w-[220px] text-xs leading-5">
                    Ajoutez une image pour valoriser la publication.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Publication
            </p>

            <button
              type="button"
              onClick={() => onChange("is_active", !form.is_active)}
              className={`mt-3 flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition ${
                form.is_active
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
              }`}
            >
              <span>
                <span className="block text-sm font-bold">
                  {form.is_active
                    ? "Publication publiée"
                    : "Publication en brouillon"}
                </span>

                <span className="mt-0.5 block text-xs opacity-75">
                  {form.is_active
                    ? "Elle sera visible sur le site public."
                    : "Elle restera masquée du public."}
                </span>
              </span>

              <span
                className={`h-6 w-11 rounded-full p-1 transition ${
                  form.is_active ? "bg-emerald-500" : "bg-amber-500"
                }`}
              >
                <span
                  className={`block h-4 w-4 rounded-full bg-white transition ${
                    form.is_active ? "translate-x-5" : ""
                  }`}
                />
              </span>
            </button>
          </div>

          <div className="rounded-[1.7rem] border border-sky-100 bg-sky-50 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
            <p className="text-sm font-bold text-sky-900 dark:text-sky-200">
              Conseils pratiques
            </p>

            <ul className="mt-3 space-y-2">
              {PUBLICATIONS_FORM_HELPERS.map((helper) => (
                <li
                  key={helper}
                  className="text-sm leading-6 text-sky-800 dark:text-sky-200"
                >
                  • {helper}
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </AdminDrawer>
  );
}